import { useEffect, useRef, useState } from 'react'
import { Check } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { usePromoStatus } from '@/hooks/use-promo-status'
import type { usePromoCode } from '@/hooks/use-promo-code'
import { tiers } from '@/lib/plans'
import { PROMO_COPY, promoDiscountedPrice, promoSentence, toPromoInterval } from '@/lib/promo'

type PromoController = ReturnType<typeof usePromoCode>

/**
 * "Have a promo code?" at checkout: a text link that opens one field. The field asks on Enter and on blur,
 * and says `N% off your first year` or `This code is not valid.` It renders nothing while the feature is off.
 * The state lives in `usePromoCode`, so the plan picker can read the code at the buy button.
 */
export function PromoField({ promo }: { promo: PromoController }) {
  const { enabled } = usePromoStatus()
  const [open, setOpen] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) input.current?.focus()
  }, [open])

  if (!enabled) return null

  const { state, busy } = promo
  const hasAnswer = state.phase === 'valid' || state.phase === 'invalid' || state.phase === 'limited' || state.phase === 'error'

  if (!open) {
    return (
      <div className="mb-4 flex justify-center">
        <button type="button" className="text-sm text-primary hover:underline" onClick={() => setOpen(true)}>
          {PROMO_COPY.link}
        </button>
      </div>
    )
  }

  return (
    <div className="mx-auto mb-6 w-full max-w-sm space-y-1 text-left">
      <Label htmlFor="promo-code">{PROMO_COPY.label}</Label>
      <Input
        ref={input}
        id="promo-code"
        name="promo-code-field"
        value={promo.code}
        // `readOnly`, not `disabled`: a disabled field loses focus, and focus must stay here.
        readOnly={busy}
        aria-disabled={busy}
        aria-busy={busy}
        aria-invalid={state.phase === 'invalid'}
        aria-describedby="promo-code-result"
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        onChange={(e) => promo.setCode(e.target.value)}
        onBlur={promo.blur}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return
          // Enter checks the code; it never submits a form around the picker.
          e.preventDefault()
          promo.submit()
        }}
      />
      <div id="promo-code-result" aria-live="polite" className="min-h-5 text-sm">
        {busy && <p className="text-muted-foreground">{PROMO_COPY.checking}</p>}
        {state.phase === 'valid' && <PromoAnswer state={state} />}
        {hasAnswer && state.phase !== 'valid' && <p className="text-destructive">{state.message}</p>}
      </div>
    </div>
  )
}

function PromoAnswer({ state }: { state: Extract<PromoController['state'], { phase: 'valid' }> }) {
  const { terms, plan, interval } = state
  const price = promoDiscountedPrice(plan, interval, terms.percent_off)
  const name = tiers.find((t) => t.plan === plan)?.name ?? plan
  return (
    <>
      <p className="flex items-center gap-1 text-status-up">
        <Check className="h-4 w-4 shrink-0" aria-hidden="true" />
        {promoSentence(terms, toPromoInterval(interval))}
      </p>
      <p className="text-body">
        {name}:{' '}
        <s className="num text-muted-foreground">
          <span className="sr-only">was </span>
          {price.original}
        </s>{' '}
        <span className="num font-semibold text-foreground">
          <span className="sr-only">now </span>
          {price.discounted}
        </span>{' '}
        per {price.per === 'yr' ? 'year' : 'month'}. {PROMO_COPY.priceNote}
      </p>
    </>
  )
}
